import * as React from 'react';
import { classes, media, style } from 'typestyle';
import { px } from 'csx';
import { ContentSliceProps } from './slices/ContentSlice';
import { PlaylistTile } from '../molecules/PlaylistTile';
import { ArtistSchema, PlaylistGroupPlaylistSchema, PlaylistSchema, TrackSchema } from '../../../types/schema';
import { DecoratedPageCarousel, DecoratedPageCarouselProps } from './DecoratedPageCarousel';
import { colorGunmetal, rebrand } from '../../../theme/color';
import FavouritePlaylistButtonCompat from '../../compat/favourite-playlist-button-compat';
import { ArtistTile } from '../molecules/ArtistTile';
import { SuggestionType } from '../atoms/SearchSuggestionItem';
import TitleText from '../atoms/TitleText';
import { end, flexRoot } from 'csstips';
import { spacing } from '../../../theme/spacing';
import { TrackTile } from '../molecules/TrackTile';
import { FavouriteButtonCompat } from '../../compat/favourite-button-compat';
import PlayButton from '../atoms/button/PlayButton';
import { NonActiveArtistTile } from '../molecules/NonActiveArtistTile';
import { AddRemoveCompat } from '../../compat/add-remove-compat';

const styles = {
  carouselControls: style({
    $nest: {
      '& > button': {
        transform: 'translateY(-34px)',
      },
    },
  }),
  tileClassName: style(
    media(
      {
        maxWidth: 250,
      },
      { width: '78%' }
    ),
    {
      minWidth: '166px',
    }
  ),
  resultHeader: style(flexRoot, end, {
    height: px(48),
    paddingBottom: spacing.DEFAULT,
  }),
  heading: style({
    lineHeight: 1,
    marginBottom: 0,
  }),
  featuredIn: style({
    color: rebrand.contrast[40].toString(),
    whiteSpace: 'nowrap',
  }),
  trackTile: style({
    $nest: {
      ':not(.with-gradient) &': {
        color: colorGunmetal.toHexString(),
      },
    },
  }),
};

export type TopSolutionEntity = (PlaylistGroupPlaylistSchema | PlaylistSchema | ArtistSchema | TrackSchema) & {
  type: SuggestionType;
  is_non_active_artist?: boolean;
};

type CarouselSliceProps = ContentSliceProps &
  DecoratedPageCarouselProps & {
    topSolutions: Array<TopSolutionEntity>;
    actionHref?: string;
    selectedEntity?: SuggestionType;
    onSelectTopSolution: (a: string, b: string) => void;
  };

export const TopSolutionCarousel: React.FC<CarouselSliceProps> = ({
  className,
  contentClassName,
  topSolutions,
  carouselClassName,
  selectedEntity,
  onSelectTopSolution,
  ...props
}) => {
  if (!topSolutions.length) return null;

  const getRelatedPlaylistsHeading = (): string => {
    switch (selectedEntity) {
      case 'artist':
        return 'Artist features in';
      case 'track':
        return 'Track features in';
      case 'playlist':
        return 'Similar playlists';
      default:
        return null;
    }
  };

  return (
    <DecoratedPageCarousel
      actions={null}
      controlClassName={styles.carouselControls}
      carouselClassName={classes(carouselClassName)}
      indicatorAlignment="centre"
      tileClassName={styles.tileClassName}
      mobileTileSize={40.5}
      withControls={false}
      indicators={false}
      {...props}
    >
      {topSolutions.map((topSolution: TopSolutionEntity, i: number) => {
        const isTopResult = topSolution.type === selectedEntity && i === 0;
        const isFeaturedResult = !isTopResult && i === 1;

        if (topSolution.type === 'playlist') {
          const category = isTopResult ? 'top result' : isFeaturedResult ? getRelatedPlaylistsHeading() : '';

          return (
            <div
              key={`playlist_${topSolution.identity}`}
              data-test-top-solution={isTopResult ? 'playlist' : 'feaured-playlist'}
            >
              <div className={styles.resultHeader}>
                {isTopResult ? (
                  <TitleText text="Top Result" className={styles.heading} />
                ) : isFeaturedResult ? (
                  <TitleText
                    text={getRelatedPlaylistsHeading()}
                    className={classes(styles.heading, styles.featuredIn)}
                    data-test-header={getRelatedPlaylistsHeading()}
                  />
                ) : null}
              </div>
              <PlaylistTile
                onClick={() => onSelectTopSolution(category, `browse/collection/${topSolution.slug}`)}
                playlist={topSolution}
                className={styles.trackTile}
                size="large"
                searchPage
                usePlaceholderImage
              >
                <FavouritePlaylistButtonCompat identity={topSolution.identity} />
              </PlaylistTile>
            </div>
          );
        }

        if (topSolution.type === 'artist') {
          const artist = topSolution as ArtistSchema;

          return (
            <div key={`artist_${artist.identity}`} data-test-top-solution="artist">
              <div className={styles.resultHeader}>
                {isTopResult && <TitleText text="Top result" className={styles.heading} />}
                {isFeaturedResult && (
                  <TitleText text="Similar artists" className={classes(styles.heading, styles.featuredIn)} />
                )}
              </div>

              {artist.is_non_active_artist ? (
                <NonActiveArtistTile isSearchPage artist={artist} />
              ) : (
                <ArtistTile
                  isSearchPage
                  artist={artist}
                  onClick={() => onSelectTopSolution('Top Solution', `music/artists/${artist.slug}`)}
                />
              )}
            </div>
          );
        }

        if (topSolution.type === 'track') {
          const track = topSolution as TrackSchema;

          return (
            <div key={`track_${track.identity}`} data-test-top-solution="artist">
              <div className={styles.resultHeader}>
                {isTopResult && <TitleText text="Top result" className={styles.heading} />}
              </div>

              <TrackTile
                track={track}
                isSearchPage
                onClick={() =>
                  onSelectTopSolution('Top Solution', `music/artists/${track.artists[0].slug}/track/${track.slug}`)
                }
              >
                <FavouriteButtonCompat track={track} />
                <PlayButton track={track} />
                <AddRemoveCompat track={track} size={40} className="basket" firedFromTrackCarousel={true} />
              </TrackTile>
            </div>
          );
        }
      })}
    </DecoratedPageCarousel>
  );
};
